import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import type { PmmSession } from '../domain/auth'
import { clearAllPmmSensitiveDeviceState } from '../offline/encryptedVault'
import { createAuthGateway, type AuthGateway, type PrototypeRole } from './authGateway'

interface AuthSessionValue {
  session: PmmSession | null
  loading: boolean
  mode: AuthGateway['mode']
  signIn(email: string, password: string, prototypeRole?: PrototypeRole): Promise<void>
  registerPartner(email: string, password: string): Promise<void>
  claimPartnerInvite(token: string): Promise<void>
  reauthenticate(password: string): Promise<void>
  claimOwner(): Promise<void>
  signOut(): Promise<void>
}

const AuthSessionContext = createContext<AuthSessionValue | null>(null)
const defaultGateway = createAuthGateway()

export function AuthSessionProvider({ children, gateway = defaultGateway }: { children: ReactNode; gateway?: AuthGateway }) {
  const [session, setSession] = useState<PmmSession | null>(null)
  const [loading, setLoading] = useState(true)
  const previousOwnerUid = useRef<string | null>(null)

  useEffect(() => gateway.subscribe((next) => {
    if (previousOwnerUid.current && previousOwnerUid.current !== next?.uid) void clearAllPmmSensitiveDeviceState()
    previousOwnerUid.current = next?.uid ?? null
    setSession(next)
    setLoading(false)
  }), [gateway])
  const safeSignOut = async () => { await clearAllPmmSensitiveDeviceState(); await gateway.signOut() }

  const value = useMemo<AuthSessionValue>(() => ({
    session,
    loading,
    mode: gateway.mode,
    signIn: (email, password, prototypeRole) => gateway.signIn(email, password, prototypeRole),
    registerPartner: (email, password) => gateway.registerPartner(email, password),
    claimPartnerInvite: (token) => gateway.claimPartnerInvite(token),
    reauthenticate: (password) => gateway.reauthenticate(password),
    claimOwner: () => gateway.claimOwner(),
    signOut: safeSignOut
  }), [gateway, session, loading])

  return <AuthSessionContext.Provider value={value}>{children}</AuthSessionContext.Provider>
}

export function useAuthSession(): AuthSessionValue {
  const value = useContext(AuthSessionContext)
  if (!value) throw new Error('useAuthSession must be used inside AuthSessionProvider')
  return value
}
